/**
 * Activité des sociétés (super administrateur).
 *
 * Un tableau comparatif pour repérer les sociétés qui n'utilisent plus
 * l'application: utilisateurs, camions, trajets sur 30 jours, dernière
 * activité et licence. Les sociétés de démonstration restent visibles mais ne
 * comptent pas parmi les sociétés sans activité.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronsUpDown, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';

import { adminAPI } from '../lib/api';
import { libelle } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { accorde, enDate, formatDate, formatDateHeure, formatNombre } from '../lib/formats';
import { PageHeader } from '../components/ui/page-header';
import { StatutBadge } from '../components/ui/StatutBadge';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { BarrePagination } from '../components/ui/pagination-bar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { usePagination } from '../hooks/usePagination';

const JOUR = 86400000;
/** Une société sans activité depuis ce nombre de jours est inactive. */
export const SEUIL_INACTIVE_JOURS = 14;
/** Fenêtre d'alerte avant la fin d'une licence. */
const LICENCE_BIENTOT_JOURS = 30;

const horodatage = (valeur) => {
  const date = enDate(valeur);
  return date ? date.getTime() : null;
};

/**
 * État d'une société: `suspendue`, `jamais` (aucune activité), `inactive`
 * (plus d'activité depuis `jours` jours) ou `active`.
 */
export function etatSociete(ligne, maintenant = Date.now()) {
  if (!ligne.is_active) return { code: 'suspendue', jours: null, rang: 3 };
  const derniere = horodatage(ligne.last_activity_at);
  if (derniere === null) return { code: 'jamais', jours: null, rang: 2 };
  const jours = Math.max(0, Math.floor((maintenant - derniere) / JOUR));
  if (jours >= SEUIL_INACTIVE_JOURS) return { code: 'inactive', jours, rang: 1 };
  return { code: 'active', jours, rang: 0 };
}

/** Société active mais qui n'a plus servi: comptée parmi les sociétés sans activité. */
const estSansActivite = (ligne, maintenant) => {
  const { code } = etatSociete(ligne, maintenant);
  return code === 'inactive' || code === 'jamais';
};

const COLONNES = [
  { cle: 'name', label: 'Société' },
  { cle: 'type', label: 'Activité' },
  { cle: 'users_count', label: 'Utilisateurs', numerique: true },
  { cle: 'trucks_count', label: 'Camions', numerique: true },
  { cle: 'trips_30d', label: 'Trajets (30 j)', numerique: true },
  { cle: 'last_activity_at', label: 'Dernière activité' },
  { cle: 'license', label: 'Licence' },
  { cle: 'etat', label: 'État' },
];

const comparer = (cle, a, b, maintenant) => {
  switch (cle) {
    case 'name':
      return String(a.name || '').localeCompare(String(b.name || ''), 'fr', { sensitivity: 'base' });
    case 'type':
      return libelle('orgType', a.type, '').localeCompare(libelle('orgType', b.type, ''), 'fr');
    case 'last_activity_at': {
      // Jamais d'activité = le plus ancien.
      const x = horodatage(a.last_activity_at);
      const y = horodatage(b.last_activity_at);
      return (x ?? -Infinity) - (y ?? -Infinity);
    }
    case 'license': {
      const x = horodatage(a.license?.expires_at);
      const y = horodatage(b.license?.expires_at);
      return (x ?? Infinity) - (y ?? Infinity);
    }
    case 'etat':
      return etatSociete(a, maintenant).rang - etatSociete(b, maintenant).rang;
    default:
      return (Number(a[cle]) || 0) - (Number(b[cle]) || 0);
  }
};

function BadgeEtat({ ligne, maintenant }) {
  const etat = etatSociete(ligne, maintenant);
  if (etat.code === 'suspendue') return <StatutBadge domaine="licenseStatus" code="SUSPENDED" />;
  if (etat.code === 'jamais') return <StatutBadge ton="warning">Jamais utilisée</StatutBadge>;
  if (etat.code === 'inactive') {
    return <StatutBadge ton="warning">{`Inactive depuis ${accorde(etat.jours, 'jour')}`}</StatutBadge>;
  }
  return <StatutBadge domaine="licenseStatus" code="ACTIVE" />;
}

function EnteteTri({ colonne, tri, onTrier }) {
  const actif = tri.cle === colonne.cle;
  const Icone = !actif ? ChevronsUpDown : tri.sens === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead
      aria-sort={!actif ? 'none' : tri.sens === 'asc' ? 'ascending' : 'descending'}
      className={colonne.numerique ? 'text-right' : undefined}
    >
      <button
        type="button"
        onClick={() => onTrier(colonne.cle)}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.06em] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          colonne.numerique ? 'flex-row-reverse' : ''
        }`}
      >
        {colonne.label}
        <Icone className="h-3 w-3 shrink-0" aria-hidden="true" />
      </button>
    </TableHead>
  );
}

export default function ActiviteSocietesPage() {
  const [lignes, setLignes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [inactivesSeulement, setInactivesSeulement] = useState(false);
  const [tri, setTri] = useState({ cle: 'last_activity_at', sens: 'asc' });
  const [maintenant, setMaintenant] = useState(() => Date.now());

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const data = await adminAPI.getOrganizationsOverview();
      setLignes(Array.isArray(data?.items) ? data.items : []);
      setMaintenant(Date.now());
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de charger l’activité des sociétés'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const chiffres = useMemo(() => {
    const reelles = lignes.filter((l) => !l.is_demo);
    const limite = maintenant + LICENCE_BIENTOT_JOURS * JOUR;
    return {
      actives: reelles.filter((l) => l.is_active).length,
      sansActivite: reelles.filter((l) => l.is_active && estSansActivite(l, maintenant)).length,
      licences: lignes.filter((l) => {
        const fin = horodatage(l.license?.expires_at);
        return fin !== null && fin >= maintenant && fin <= limite;
      }).length,
    };
  }, [lignes, maintenant]);

  const visibles = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    const filtrees = lignes.filter((l) => {
      if (terme && !String(l.name || '').toLowerCase().includes(terme)) return false;
      if (inactivesSeulement && (l.is_demo || !l.is_active || !estSansActivite(l, maintenant))) return false;
      return true;
    });
    const facteur = tri.sens === 'asc' ? 1 : -1;
    return [...filtrees].sort(
      (a, b) =>
        facteur * comparer(tri.cle, a, b, maintenant) ||
        String(a.name || '').localeCompare(String(b.name || ''), 'fr', { sensitivity: 'base' }),
    );
  }, [lignes, recherche, inactivesSeulement, tri, maintenant]);

  const pagination = usePagination(visibles, {
    reinitialiser: `${recherche}|${inactivesSeulement}|${tri.cle}|${tri.sens}`,
    cle: 'activite',
  });

  const trier = (cle) =>
    setTri((precedent) =>
      precedent.cle === cle
        ? { cle, sens: precedent.sens === 'asc' ? 'desc' : 'asc' }
        : { cle, sens: cle === 'name' || cle === 'type' ? 'asc' : 'desc' },
    );

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Plateforme"
        title="Activité des sociétés"
        description="Compare l’usage de chaque société pour repérer celles qui n’utilisent plus l’application. Les sociétés de démonstration ne comptent pas parmi les sociétés inactives."
        stats={[
          { label: 'Sociétés actives', valeur: chiffres.actives, ton: 'positif' },
          {
            label: `Sans activité depuis ${SEUIL_INACTIVE_JOURS} jours`,
            valeur: chiffres.sansActivite,
            ton: chiffres.sansActivite ? 'attention' : 'neutre',
            onClick: () => setInactivesSeulement(true),
          },
          {
            label: `Licences expirant sous ${LICENCE_BIENTOT_JOURS} jours`,
            valeur: chiffres.licences,
            ton: chiffres.licences ? 'attention' : 'neutre',
          },
        ]}
        actions={
          <Button variant="outline" onClick={charger} disabled={chargement}>
            <RefreshCw className={`h-4 w-4 ${chargement ? 'animate-spin' : ''}`} aria-hidden="true" />
            Rafraîchir
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            type="search"
            aria-label="Rechercher une société"
            placeholder="Rechercher une société"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-card pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <Button
          type="button"
          size="sm"
          variant={inactivesSeulement ? 'default' : 'outline'}
          aria-pressed={inactivesSeulement}
          onClick={() => setInactivesSeulement((v) => !v)}
        >
          Inactives seulement
        </Button>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              {COLONNES.map((colonne) => (
                <EnteteTri key={colonne.cle} colonne={colonne} tri={tri} onTrier={trier} />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {chargement && lignes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLONNES.length} className="py-6 text-center text-muted-foreground">
                  Chargement…
                </TableCell>
              </TableRow>
            ) : visibles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLONNES.length} className="py-6 text-center text-muted-foreground">
                  Aucune société
                </TableCell>
              </TableRow>
            ) : (
              pagination.lignes.map((l) => (
                <TableRow key={l.id} data-testid="ligne-societe">
                  <TableCell className="font-medium">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link to={`/organizations/${l.id}`} className="hover:underline">
                        {l.name}
                      </Link>
                      {l.is_demo ? <Badge variant="muted">Démonstration</Badge> : null}
                    </div>
                  </TableCell>
                  <TableCell>{libelle('orgType', l.type, 'Activité à préciser')}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNombre(l.users_count)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNombre(l.trucks_count)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNombre(l.trips_30d)}</TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {formatDateHeure(l.last_activity_at, 'Jamais')}
                  </TableCell>
                  <TableCell>
                    {l.license ? (
                      <div className="leading-tight">
                        <span>{libelle('licensePlan', l.license.plan, 'Licence')}</span>
                        <span className="block text-xs tabular-nums text-muted-foreground">
                          {formatDate(l.license.expires_at)}
                        </span>
                      </div>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <BadgeEtat ligne={l} maintenant={maintenant} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {!chargement && visibles.length > 0 ? (
        <BarrePagination pagination={pagination} nom="sociétés" className="mt-0" />
      ) : null}
    </div>
  );
}
